import { CustomAttributes } from "../../../pojo/CustomAttributes";
import { S25Util } from "../../../util/s25-util";
import { Operators, UnaryOperators } from "../../s25-dropdown/single-select/s25.search.operators.dropdown.component";

/** One applied contact custom attribute filter (maps to {@code cust_atrb_filters} tokens). */
export type SeUserSearchCustomAttributeFilter = {
    filterKey: string;
    custAtrbId: number;
    custAtrbName?: string;
    /** UI-only; drives value widget on reseed (not sent to API). */
    custAtrbType?: string | number;
    operatorOffset: number;
    operatorLabel?: string;
    value?: string;
};

/** Row model shape used by {@link S25qlSearchAdvancedCustomAttributeComponent}. */
export type SeUserSearchCustomAttributeModel = {
    cust_atrb_id: number | null;
    cust_atrb_name?: string | null;
    cust_atrb_type: string | number | null;
    itemId?: string | number | Date | null;
    itemName?: string | null;
    relationship_type_id: number | null;
    relationship_type_name?: string | null;
};

export type SeUserSearchCustomAttributeRow = {
    rowKey: number;
    model: SeUserSearchCustomAttributeModel;
    source: "applied" | "draft";
};

export function emptySeUserSearchCustomAttributeModel(): SeUserSearchCustomAttributeModel {
    return {
        cust_atrb_id: null,
        cust_atrb_name: null,
        cust_atrb_type: null,
        itemId: null,
        relationship_type_id: null,
        relationship_type_name: null,
    };
}

/** Clears stale value/operator fields when the user changes attribute before picking a new operator. */
export function normalizeSeUserSearchCustomAttributeModelForEdit(
    model: SeUserSearchCustomAttributeModel,
): SeUserSearchCustomAttributeModel {
    if (model.relationship_type_id != null && Number.isFinite(model.relationship_type_id)) {
        return { ...model };
    }
    return {
        ...model,
        itemId: null,
        itemName: null,
        relationship_type_name: null,
    };
}

/** Incomplete edits stay as draft rows so syncRowsFromApplied does not drop them. */
export function resolveSeUserSearchCustomAttributeRowSourceAfterEdit(
    model: SeUserSearchCustomAttributeModel,
    previousSource: SeUserSearchCustomAttributeRow["source"],
): SeUserSearchCustomAttributeRow["source"] {
    if (!isCompleteSeUserSearchCustomAttributeModel(model)) {
        return "draft";
    }
    return previousSource;
}

export function isCompleteSeUserSearchCustomAttributeModel(
    model: SeUserSearchCustomAttributeModel | null | undefined,
): boolean {
    if (!model?.cust_atrb_id || !Number.isFinite(Number(model.cust_atrb_id))) {
        return false;
    }
    if (!model.relationship_type_id || !Number.isFinite(model.relationship_type_id)) {
        return false;
    }
    if (isSeUserSearchModelUnaryRelationship(model.relationship_type_id)) {
        return true;
    }
    const itemId = model.itemId;
    if (itemId === null || itemId === undefined || String(itemId).trim() === "") {
        return false;
    }
    if ((itemId === "T" || itemId === "F") && model.cust_atrb_type !== CustomAttributes.Type.Boolean) {
        return false;
    }
    return true;
}

export function toSeUserSearchCustomAttributeFilter(
    rowKey: number,
    model: SeUserSearchCustomAttributeModel,
): SeUserSearchCustomAttributeFilter | null {
    if (!isCompleteSeUserSearchCustomAttributeModel(model)) {
        return null;
    }
    const relationshipTypeId = model.relationship_type_id!;
    const operatorOffset = resolveSeUserSearchCustomAttributeOperatorOffset(relationshipTypeId);
    const value = resolveSeUserSearchCustomAttributeFilterValue(model, relationshipTypeId, operatorOffset);
    if (!isSeUserSearchApiUnaryOperator(operatorOffset) && (value === undefined || value === "")) {
        return null;
    }
    return {
        filterKey: String(rowKey),
        custAtrbId: Number(model.cust_atrb_id),
        custAtrbName: model.cust_atrb_name ?? undefined,
        custAtrbType: model.cust_atrb_type ?? undefined,
        operatorOffset,
        operatorLabel: model.relationship_type_name ?? undefined,
        value,
    };
}

export function buildCustomAttributeFiltersParam(
    filters: SeUserSearchCustomAttributeFilter[] | null | undefined,
): string | undefined {
    const tokens =
        filters
            ?.map((filter) => {
                if (!filter?.custAtrbId || !Number.isFinite(filter.custAtrbId)) {
                    return null;
                }
                if (!Number.isFinite(filter.operatorOffset)) {
                    return null;
                }
                if (isSeUserSearchApiUnaryOperator(filter.operatorOffset)) {
                    return `${filter.custAtrbId}:${filter.operatorOffset}`;
                }
                if (filter.value === undefined || filter.value === null || filter.value === "") {
                    return null;
                }
                return `${filter.custAtrbId}:${filter.operatorOffset}:${encodeURIComponent(filter.value)}`;
            })
            .filter((token): token is string => !!token) ?? [];
    return tokens.length ? tokens.join(",") : undefined;
}

export function formatSeUserSearchCustomAttributeFilterLabel(filter: SeUserSearchCustomAttributeFilter): string {
    const name = filter.custAtrbName?.trim() || `Attribute ${filter.custAtrbId}`;
    const operatorLabel = filter.operatorLabel?.trim() || operatorOffsetLabel(filter.operatorOffset);
    if (isSeUserSearchApiUnaryOperator(filter.operatorOffset)) {
        return `${name} ${operatorLabel}`;
    }
    const valueLabel = filter.value?.trim();
    return valueLabel ? `${name} ${operatorLabel} ${valueLabel}` : `${name} ${operatorLabel}`;
}

export function collectSeUserSearchCustomAttributeFiltersFromRows(
    rows: SeUserSearchCustomAttributeRow[],
): SeUserSearchCustomAttributeFilter[] {
    return (
        rows
            .map((row) => toSeUserSearchCustomAttributeFilter(row.rowKey, row.model))
            .filter((filter): filter is SeUserSearchCustomAttributeFilter => filter !== null) ?? []
    );
}

/** True when two applied sets serialize to the same cust_atrb_filters param (filterKey ignored). */
export function areSeUserSearchCustomAttributeFiltersEqual(
    left: SeUserSearchCustomAttributeFilter[] | null | undefined,
    right: SeUserSearchCustomAttributeFilter[] | null | undefined,
): boolean {
    return buildCustomAttributeFiltersParam(left) === buildCustomAttributeFiltersParam(right);
}

export function resolveSeUserSearchCustomAttributeRowKey(filterKey: string, fallbackRowKey: number): number {
    const parsedRowKey = Number.parseInt(filterKey, 10);
    if (Number.isFinite(parsedRowKey) && parsedRowKey > 0) {
        return parsedRowKey;
    }
    return fallbackRowKey;
}

export function seUserSearchCustomAttributeRowsFromFilters(
    filters: SeUserSearchCustomAttributeFilter[] | null | undefined,
): SeUserSearchCustomAttributeRow[] {
    if (!filters?.length) {
        return [];
    }
    let fallbackRowKey = 0;
    return filters.map((filter) => {
        fallbackRowKey += 1;
        const rowKey = resolveSeUserSearchCustomAttributeRowKey(filter.filterKey, fallbackRowKey);
        const relationshipTypeId = resolveRelationshipTypeIdFromFilter(filter);
        return {
            rowKey,
            source: "applied",
            model: {
                cust_atrb_id: filter.custAtrbId,
                cust_atrb_name: filter.custAtrbName ?? null,
                cust_atrb_type: filter.custAtrbType ?? null,
                itemId:
                    filter.value ??
                    (filter.operatorOffset === Operators.Exists
                        ? "T"
                        : filter.operatorOffset === Operators.NotExists
                          ? "F"
                          : null),
                relationship_type_id: relationshipTypeId,
                relationship_type_name: filter.operatorLabel ?? operatorOffsetLabel(filter.operatorOffset),
            },
        };
    });
}

function resolveRelationshipTypeIdFromFilter(filter: SeUserSearchCustomAttributeFilter): number {
    if (filter.operatorOffset === Operators.Equals && filter.value === "T") {
        return Operators.IsTrue;
    }
    if (filter.operatorOffset === Operators.Equals && filter.value === "F") {
        return Operators.IsFalse;
    }
    return filter.operatorOffset;
}

function resolveSeUserSearchCustomAttributeOperatorOffset(relationshipTypeId: number): number {
    if (relationshipTypeId === Operators.IsTrue || relationshipTypeId === Operators.IsFalse) {
        return Operators.Equals;
    }
    return relationshipTypeId % 100;
}

function resolveSeUserSearchCustomAttributeFilterValue(
    model: SeUserSearchCustomAttributeModel,
    relationshipTypeId: number,
    operatorOffset: number,
): string | undefined {
    if (isSeUserSearchApiUnaryOperator(operatorOffset)) {
        return undefined;
    }
    if (relationshipTypeId === Operators.IsTrue) {
        return "T";
    }
    if (relationshipTypeId === Operators.IsFalse) {
        return "F";
    }
    const itemId = model.itemId;
    if (itemId === null || itemId === undefined) {
        return undefined;
    }
    const attributeType = model.cust_atrb_type;
    if (attributeType === CustomAttributes.Type.Date) {
        return S25Util.date.toS25ISODateStr(itemId) ?? undefined;
    }
    if (attributeType === CustomAttributes.Type.Time) {
        return S25Util.date.toS25ISOTimeStr(itemId) ?? undefined;
    }
    if (attributeType === CustomAttributes.Type.Datetime) {
        return S25Util.date.toS25ISODateTimeStr(itemId) ?? undefined;
    }
    return String(itemId);
}

function isSeUserSearchModelUnaryRelationship(relationshipTypeId: number): boolean {
    return UnaryOperators.includes(relationshipTypeId) || UnaryOperators.includes(relationshipTypeId % 100);
}

/** Exists / NotExists only — API tokens omit the value segment (boolean IsTrue/IsFalse still serialize as equals + T/F). */
function isSeUserSearchApiUnaryOperator(operatorOffset: number): boolean {
    return operatorOffset === Operators.Exists || operatorOffset === Operators.NotExists;
}

function operatorOffsetLabel(operatorOffset: number): string {
    switch (operatorOffset) {
        case Operators.Equals:
            return "is equal to";
        case Operators.LTE:
            return "is less than or equal to";
        case Operators.GTE:
            return "is greater than or equal to";
        case Operators.StartsWith:
            return "starts with";
        case Operators.Earlier:
            return "is earlier than or equal to";
        case Operators.Later:
            return "is later than or equal to";
        case Operators.NotExists:
            return "does not exist";
        case Operators.Exists:
            return "exists";
        case Operators.Contains:
            return "contains";
        default:
            return "matches";
    }
}
